import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useAppClient } from '../../../data/ClientProvider';
import type { FieldValue, RecordRow } from '../../../data/client';
import { queryKeys } from '../../../data/queryKeys';
import { Button, useToast } from '../../../ui';
import { type HiddenRows, useHiddenRows, useNamedFields } from './hooks';

/**
 * "MARK AS HANDLED" (redesign register D2, R-K1, L11; ledger RD2-SMALL-1; brief FE-FOUNDATION
 * section 12).
 *
 * D2: "a person decides. Button label "Mark as handled" ... The page hides the row at once ...
 * The client leaves Waiting and does not jump to Gone quiet. Undo through the existing toast.
 * The button is visible at rest, not only on hover."
 *
 * The page calls `useReplyHandled()` ONCE for its list and puts `MarkHandledAction` on each
 * Waiting row, outside the row link (`ui/DashboardRow` `actions`). A click hides the row at
 * once, writes a stamp into the agency's canonical record entry straight away (INV-13: every
 * list that reads the agency agrees before the next feed run, which is at 06, 12 and 18 Berlin
 * only), and calls the server, whose own clock is the stamp that is kept
 * (`markReplyHandled`, no body). The success toast offers Undo, as the Dashboard's task toast
 * does (`Commitments.tsx`); Undo empties the field again (`clearReplyHandled`) and brings the
 * row back - after a second mark it empties the stamp rather than restoring the first one, as
 * built on the server (open question Q-FND-15). A refused write keeps the row and shows the
 * product's danger toast with the server's sentence.
 *
 * The toast's title is "<Client> marked as handled" (open question Q-FND-9, decided: the
 * components page's placeholder).
 */
export const REPLY_HANDLED_FIELD = 'reply_handled_at';

export interface HandledAgency {
  readonly id: string;
  readonly name: string;
}

export interface ReplyHandled extends HiddenRows {
  /** False in an installation without `agency.reply_handled_at`: the action is not drawn. */
  readonly available: boolean;
  readonly mark: (agency: HandledAgency) => Promise<void>;
  readonly undo: (agency: HandledAgency) => Promise<void>;
}

export function markedToastTitle(clientName: string): string {
  return `${clientName} marked as handled`;
}

function messageOf(error: unknown): string {
  return error instanceof Error && error.message !== '' ? error.message : String(error);
}

export function useReplyHandled(): ReplyHandled {
  const client = useAppClient();
  const queryClient = useQueryClient();
  const toast = useToast();
  const rows = useHiddenRows();
  const agency = useNamedFields('agency');
  const field = agency.field(REPLY_HANDLED_FIELD);
  const objectId = agency.object?.id;
  const fieldId = field?.id;

  /** The agency's canonical entry (INV-13), read and written in one place. */
  const read = useCallback(
    (agencyId: string): FieldValue => {
      if (objectId === undefined || fieldId === undefined) return null;
      return (
        queryClient.getQueryData<RecordRow>(queryKeys.record(objectId, agencyId))?.values[
          fieldId
        ] ?? null
      );
    },
    [queryClient, objectId, fieldId],
  );
  const write = useCallback(
    (agencyId: string, value: FieldValue) => {
      if (objectId === undefined || fieldId === undefined) return;
      queryClient.setQueryData<RecordRow>(queryKeys.record(objectId, agencyId), (current) =>
        current === undefined
          ? current
          : { ...current, values: { ...current.values, [fieldId]: value } },
      );
    },
    [queryClient, objectId, fieldId],
  );

  const { hide, show } = rows;

  const undo = useCallback(
    async (target: HandledAgency) => {
      show(target.id);
      const previous = read(target.id);
      write(target.id, null);
      try {
        const answer = await client.fulfillment.clearReplyHandled(target.id);
        write(target.id, answer.replyHandledAt);
      } catch (error) {
        hide(target.id);
        write(target.id, previous);
        toast({ title: messageOf(error), tone: 'danger' });
      }
    },
    [client, hide, show, read, write, toast],
  );

  const mark = useCallback(
    async (target: HandledAgency) => {
      hide(target.id);
      const previous = read(target.id);
      write(target.id, new Date().toISOString());
      try {
        const answer = await client.fulfillment.markReplyHandled(target.id);
        write(target.id, answer.replyHandledAt);
        toast({
          title: markedToastTitle(target.name),
          tone: 'success',
          action: {
            label: 'Undo',
            altText: `Undo Mark as handled for ${target.name}`,
            onAction: () => void undo(target),
          },
        });
      } catch (error) {
        show(target.id);
        write(target.id, previous);
        toast({ title: messageOf(error), tone: 'danger' });
      }
    },
    [client, hide, show, read, write, toast, undo],
  );

  return { ...rows, available: field !== undefined, mark, undo };
}

/** The row action itself: a text action, visible at rest (D2, L11), outside the row link. */
export function MarkHandledAction({
  onMark,
  className,
}: {
  readonly onMark: () => void;
  readonly className?: string | undefined;
}) {
  return (
    <Button variant="text" onClick={onMark} className={className} data-mark-handled="">
      Mark as handled
    </Button>
  );
}
